import { useEffect, useState } from "react";
import { FixtureFinish } from "../../../models/FixtureFinish";
import { ApiResponse } from "../../../models/ApiResponse";
import api from "../../../services/ApiService";
import { SettingGrid } from "../../../components/ManageSettingComponents/SettingGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { Button, Switch } from "@mui/material";
import { toast } from "react-toastify";
import ProductFinishModal from "./ProductFinishModal";
import { useFormik } from "formik";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import * as Yup from "yup";
import { SessionDetails } from "../../../models/SessionDetails";
import { GRID_ACTION_DELETE_ICON, GRID_ACTION_EDIT_ICON } from "../../../components/GridUtilityComponents";
import { Permissions } from "../../../models/Enum";
import SessionService from "../../../services/SessionService";

export default function ProductFinishSetting() {
  const [finishList, setFinishList] = useState<FixtureFinish[]>([]);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);

  const ProductFinishGridCollumns: GridColDef[] = [
    { field: "id", headerName: "Id", width: 100 },
    { field: "finishName", headerName: "Finish Name", width: 300 },
    { field: "leagoCode", headerName: "LEAGO Code", width: 160 },
    { field: "pimKey", headerName: "PIM Key", width: 200 },
    { field: "jdeCode", headerName: "JDE Code", width: 200 },
    {
      field: "isActive",
      headerName: "isActive",
      sortable: false,
      width: 200,
      renderCell: (params: any) => (
        <>
          <Switch
            checked={params.value}
            onChange={async (e) => {
              await updateFinishActive(params.row, e.target.checked);
            }}
            color={"primary"}
            inputProps={{ "aria-label": "controlled" }}
            disabled = {!manageAppSetting}
          />
        </>
      ),
    },
    // {
    //   field: "updatedDate",
    //   headerName: "UpdatedDate",
    //   width: 200,
    //   valueFormatter: (params) => moment(params?.value).format(DATE_TIME_FORMAT),
    // },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 200,
      getActions: (params: any) => [
        GRID_ACTION_EDIT_ICON(params, handleEditClickOpen),
        GRID_ACTION_DELETE_ICON(params, handleDeleteModalOpen)
      ],
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const getFinishResponse = await api.get<ApiResponse<FixtureFinish[]>>(
      "/Picklist/finishes"
    );
    setFinishList(getFinishResponse?.data || []);
  };

  const handleEditClickOpen = (gridParams: any) => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
    formik.setValues({ ...gridParams.row });
  };

  const handleAddClickOpen = () => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
  };

  const handleDeleteModalOpen = (gridParams: any) => {
    setDeleteModalOpen(true);
    formik.setValues({ ...gridParams.row });
  };

  const handleCloseModal = () => {
    setShowAddUpdateModal(false);
    setDeleteModalOpen(false);
    formik.resetForm();
  };

  const updateFinishActive = (row: FixtureFinish, isActive: boolean) => {
    row.isActive = isActive;
    try {
      api
        .put<ApiResponse<number>>("/Picklist/finish", row)
        .then(async (response: any) => {
          if (response.isSuccess) {
            toast.success(response.message);
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const deleteSubmit = () => {
    try {
      api
        .delete<ApiResponse<SessionDetails>>(
          `/Picklist/finish/${formik.values.id}`
        )
        .then(async (response: any) => {
          if (response.isSuccess) {
            setDeleteModalOpen(false);
            formik.resetForm();
            toast.success(response.message);
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: 0,
      finishName: "",
      leagoCode: "",
      isActive: true,
      pimKey: "",
      jdeCode: "",
    },
    validationSchema: Yup.object({
      finishName: Yup.string()
        .trim()
        .min(1, "Finish Name must be at least 1 characters")
        .max(40, "Finish Name must be at most 40 characters")
        .required("Finish Name is required"),
      leagoCode: Yup.string()
        .trim()
        .required("LEAGO Code is required")
        .test("check-leagoCode-duplicate", "LEAGO Code already exists", (value) => {
          if (!value?.trim()) {
            return true;
          }
          return !finishList?.some(
            (item) =>
              item?.leagoCode?.trim().toLowerCase() === value.trim().toLowerCase() &&
              item?.id !== formik.values?.id
          );
        }),
      pimKey: Yup.string()
            .nullable()
            .max(20, "PIM Key must be at most 20 characters")
            .test("check-pimKey-duplicate", "PIM Key already exists", 
                (value) => {
                    if(!value) {
                        return true;
                    }

                    return !!value && !finishList?.some(
                        (item) => item?.pimKey && value && item?.pimKey?.trim().toLowerCase() === 
                        value?.trim().toLowerCase() && item?.id !== formik.values?.id
                    )
                }),
      jdeCode: Yup.string()
        .nullable()
        .max(20, "JDE Code must be at most 20 characters")
        .test("check-jdeCode-duplicate", "JDE Code already exists", 
            (value) => {
                if(!value) {
                    return true;
                }

                return !!value && !finishList?.some(
                    (item) => item?.jdeCode && value && item?.jdeCode?.trim().toLowerCase() === 
                    value?.trim().toLowerCase() && item?.id !== formik.values?.id
                )
            })
    }),
    onSubmit: async (values) => {
      setDisableSubmit(true);
      if (values.id > 0) {
        api
          .put<ApiResponse<number>>("/Picklist/finish", {
            id: values?.id,
            finishName: values?.finishName?.trim(),
            leagoCode: values?.leagoCode?.trim(),
            isActive: JSON.parse(`${values?.isActive}`),
            pimKey: values?.pimKey?.toString()?.trim()  || null,
            jdeCode: values?.jdeCode?.toString()?.trim()  || null,
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      } else {
        api
          .post<ApiResponse<number>>("/Picklist/finish", {
            finishName: values?.finishName?.trim(),
            leagoCode: values?.leagoCode?.trim(),
            isActive: JSON.parse(`${values?.isActive}`),
            pimKey: values?.pimKey?.toString()?.trim()  || null,
            jdeCode: values?.jdeCode?.toString()?.trim()  || null,
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      }
    },
  });

  return (
    <>
      <div>
        {manageAppSetting && <Button variant="contained" onClick={handleAddClickOpen}>
          Add Finish
        </Button>}
        <ProductFinishModal
          isOpen={showAddUpdateModal}
          onClose={handleCloseModal}
          formik={formik}
          isButtonDisabled={disableSubmit}
        />
        <DeleteValidationModal
          addModalOpen={deleteModalOpen}
          handleCloseModal={handleCloseModal}
          deleteSubmit={deleteSubmit}
          fieldIdentifier={"Finish Name"}
        />
      </div>
      <SettingGrid
        data={finishList}
        column={ProductFinishGridCollumns}
        sortByField="id"
        sortByOrder="desc"
        columnVisibility={{
          actions: manageAppSetting,
        }}
      />
    </>
  );
}
